import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import * as $ from 'jquery';
import {SiteReferenceService} from "./site-reference.service";
import * as moment from 'moment';
import {getDistanceFromLatLonInKm} from 'ceotr-web-common/helpers/math.js';
import {of} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DeploymentsService {

    inMemory = {};

    constructor(private http: HttpClient, private siteReference: SiteReferenceService) {
    }

    public getMissionSurfaceStatus() {
        const vm = this;
        return this.http.get('/gliders/api/surface_status/').toPromise()
            .then(response => {
                const ids = Object.keys(response['results']);
                const result = {};
                ids.forEach(function (id) {
                    result[id] = vm.surfaceStatusToFriendlyResult(response['results'][id]);
                });
                return result;
            })
            .catch(error => {
                console.error(error);
                return error;
            });
    }

    public surfaceStatusToFriendlyResult(data) {
        const result = Object.assign({}, data);
        if (data && data.status && data.status.current) {
            result.waypoints = {
                next: {
                    lat: data.status.current.c_wpt_lat,
                    lng: data.status.current.c_wpt_lon,
                },
                previous: {
                    lat: data.status.current.l_wpt_lat,
                    lng: data.status.current.l_wpt_lon,
                }
            };

            result.surfaceTime = new Date(data.status.current.datetime.replace(' ', 'T'));
            result.lastSurfaceTime = new Date(data.status.last.datetime.replace(' ', 'T'));
            result.battery = data.status.current.battery;
            if (data.status.last) {
                result.distanceFromLastSurfacingKm = getDistanceFromLatLonInKm(data.status.last.gps_lat, data.status.last.gps_lon, data.status.current.gps_lat, data.status.current.gps_lon);
                result.timeSinceLastSurfacingHr = (result.surfaceTime - result.lastSurfaceTime) / (1000 * 60 * 60);
                result.lastSpeedKmph = result.distanceFromLastSurfacingKm / result.timeSinceLastSurfacingHr;
                result.timeToNextWaypointHr = data.status.current.c_wpt_dist / result.lastSpeedKmph;
                const hrsToNextWaypoint = data.status.current.c_wpt_dist / result.lastSpeedKmph;
                result.nextWaypointEta = new Date(moment().add(hrsToNextWaypoint, 'hours').toISOString());
            }
            result.vacuum = data.status.current.vacuum;
            result.distanceToNextWaypointKm = data.status.current.c_wpt_dist;
        }

        return result;
    }

    public getMissionsCoordinates(deploymentNumbers) {
        const vm = this;
        const url = '/gliders/api/get_coordinates_simplified/';
        const payload = {};
        if (deploymentNumbers) {
            const paramStr = deploymentNumbers.join(',');
            payload["params"] = {deployment_nums: paramStr};
        }
        return this.http.get(url, payload).toPromise()
            .then(response => {
                const ids = Object.keys(response['results']);
                const result = {};
                ids.forEach(function (id) {
                    result[id] = vm.coordinatesToFriendlyResult(response['results'][id]);
                });
                return result;
            })
            .catch(error => {
                console.error(error);
                return error;
            })
    }

    public isValidCoordinate(coord: { lat: number, lng: number }) {
        return Math.abs(coord.lat) < 60000 && Math.abs(coord.lng) < 60000;
    }

    public coordinatesToFriendlyResult(data) {
        const coordinates = data.coordinate && data.coordinate.map(function (c) {
            return {
                lat: c[0],
                lng: c[1]
            }
        }).filter(this.isValidCoordinate) || [];

        const result = Object.assign({}, data);
        result.coordinates = coordinates;

        if (data.status && data.status.current) {
            // Add current location to coordinates to handle delay between path data source and location data source
            result.coordinates.push({
                lat: data.status.current.gps_lat,
                lng: data.status.current.gps_lon
            });
            result.waypoints = {
                next: {
                    lat: data.status.current.c_wpt_lat,
                    lng: data.status.current.c_wpt_lon,
                },
                previous: {
                    lat: data.status.current.l_wpt_lat,
                    lng: data.status.current.l_wpt_lon,
                }
            };

            result.surfaceTime = new Date(data.status.current.datetime.replace(' ', 'T'));
            result.lastSurfaceTime = new Date(data.status.last.datetime.replace(' ', 'T'));
            result.battery = data.status.current.battery;
            if (data.status.last) {
                result.distanceFromLastSurfacingKm = getDistanceFromLatLonInKm(data.status.last.gps_lat, data.status.last.gps_lon, data.status.current.gps_lat, data.status.current.gps_lon);
                result.timeSinceLastSurfacingHr = (result.surfaceTime - result.lastSurfaceTime) / (1000 * 60 * 60);
                result.lastSpeedKmph = result.distanceFromLastSurfacingKm / result.timeSinceLastSurfacingHr;
                result.timeToNextWaypointHr = data.status.current.c_wpt_dist / result.lastSpeedKmph;
                const hrsToNextWaypoint = data.status.current.c_wpt_dist / result.lastSpeedKmph;
                result.nextWaypointEta = new Date(moment().add(hrsToNextWaypoint, 'hours').toISOString());
            }
            result.vacuum = data.status.current.vacuum;
            result.distanceToNextWaypointKm = data.status.current.c_wpt_dist;
        }

        if (result.coordinates && result.coordinates.length > 0) {
            result.lastCoordinates = coordinates[coordinates.length - 1]
        }

        delete result.coordinate;

        return result;
    }

    public getAllDeployments() {
        return this.getDeployments(null);
    }

    public getErddapExceptionReason() {
        const restUrl = '/gliders/api/erddap_exception_reason/';
        return this.http.get(restUrl).toPromise().then(response => {
            return response;
        }).catch(error => {
            return error;
        });
    }

    public getDeployments(ids) {
        const restUrl = '/gliders/api/deployments/';
        return this.http.get(restUrl).toPromise()
            .then(response => {
                let result = response['glider_groups'] as GliderGroup[];
                if (ids) {
                    // @ts-ignore
                    result = result.filter(g => Array.contains(ids, g.deployment_number));
                }
                result.forEach(this.fillMission);
                return result;
            })
            .catch(error => {
                console.error(error);
                return error;
            });
    }

    public getMissionNotes(deploymentNumber) {
        return this.http.get('/gliders/api/mission_notes/' + deploymentNumber + '/').toPromise()
            .then(response => response)
            .catch(error => {
                console.error(error);
                return error;
            });
    }

    public fillMission(mission: GliderGroup) {
        if (!mission) {
            return;
        }

        mission.startTime = new Date(mission.start_time.replace(' ', 'T') + 'Z');
        mission.endTime = mission.end_time ? new Date(mission.end_time.replace(' ', 'T') + 'Z') : null;

        const endMoment = moment(mission.endTime ? mission.endTime : new Date());
        const startMoment = moment(mission.startTime);
        mission.travelDuration = {
            days: endMoment.diff(startMoment, 'days', true),
            hours: endMoment.diff(startMoment, 'hours', true) % 24
        };
    }

    public getKmlPaths(gliderGroups: GliderGroup[]) {
        const kmlUrl = this.siteReference.kmlsUrl;
        const backupKmlUrl = this.siteReference.backupKmlsUrl;
        // first get KMLs from the old site
        return this.http.get(backupKmlUrl, {responseType: 'text' as 'text'}).toPromise()
            .then(response => {
                const $result = $(response);
                const $links = $result.find('a');
                const kmlPaths = {
                    numberedMissions: {},
                    currentMissions: {},
                    other: {}
                };
                const kmlPathsOld = {
                    numberedMissions: {},
                    currentMissions: {},
                    other: {}
                };
                for (let i = 0; i < $links.length; i++) {
                    const pathname = $links[i]['pathname'];
                    if (pathname && pathname.endsWith('.kml')) {
                        const data = {
                            fullUrl: backupKmlUrl + pathname,
                            filename: pathname.substring(1)
                        };

                        let matches = data.filename.match(/^m([0-9]+)_(\w+)_/);
                        if (matches && matches.length == 3) {
                            const missionNumPart = matches[1];
                            data['missionNumber'] = parseInt(missionNumPart);
                            data['platformName'] = matches[2];

                            kmlPathsOld.numberedMissions[data['missionNumber']] = data;
                        } else {
                            matches = data.filename.match(/^(\w+).kml$/);
                            if (matches && matches.length == 2) {
                                const namePart = matches[1].toLowerCase();
                                if (this.siteReference.platformNames.indexOf(namePart) >= 0) {
                                    data['platformName'] = namePart;
                                    kmlPathsOld.currentMissions[namePart] = data;
                                } else {
                                    kmlPathsOld.other[namePart] = data;
                                }
                            } else {
                                kmlPathsOld.other[data.filename.replace('.kml', '')] = data;
                            }
                        }
                    }
                }

                // then get local KMLs generated by kml_tool
                return this.http.get(kmlUrl).toPromise()
                    .then(response => {
                        const links = response as string[];
                        links.forEach(link => {
                            // get just the trailing filename from the url
                            const data = {
                                fullUrl: link,
                                filename: link.match(/([^/]*)\.kml$/)![1],
                            };
                            // the filename format is declared in tasks.py -> process_kml_files
                            const split = data.filename.split('_');
                            let foundMatch = false;
                            if (split.length == 4) {
                                // filename has platform, SFMC date, and first and last surfacings dates
                                const [platform, sfmcDateStr, firstSurfacingDateStr, lastSurfacingDateStr] = split;
                                const sfmcDate = new Date(sfmcDateStr);
                                const firstSurfacingDate = new Date(firstSurfacingDateStr);
                                const lastSurfacingDate = new Date(lastSurfacingDateStr);
                                /*
                                Sometimes the mission start time is on the same day as these dates but a few hours earlier.
                                There won't be two real glider missions by the same glider on the same day, so just
                                rewind the SFMC date and the first surfacing date to the start of the day to catch missions
                                that don't quite line up.
                                 */
                                sfmcDate.setHours(0, 0, 0, 0);
                                firstSurfacingDate.setHours(0, 0, 0, 0);
                                lastSurfacingDate.setHours(0, 0, 0, 0);
                                /*
                                Match files to missions by platform and date.
                                If
                                - the platform matches, and
                                - the mission start date is after the earlier of the SFMC
                                  start date and the first surfacing date, and
                                - the mission start date is before the last surfacing date
                                then it's a match.
                                 */
                                gliderGroups.forEach(gliderGroup => {
                                    if (gliderGroup.platform_name.equalsIgnoreCase(platform)) {
                                        // ignore time, only look at date
                                        const startDate = new Date(gliderGroup.startTime.getTime());
                                        startDate.setHours(0, 0, 0, 0);
                                        if ((sfmcDate <= startDate || firstSurfacingDate <= startDate) &&
                                            lastSurfacingDate >= startDate) {
                                            kmlPaths.numberedMissions[gliderGroup.deployment_number] = data;
                                            foundMatch = true;
                                        }
                                    }
                                });
                            } else if (split.length == 2) {
                                // the KML file has no surfacings, so we look at the SFMC date only
                                const [platform, sfmcDateStr] = split;
                                const sfmcDate = new Date(sfmcDateStr);
                                gliderGroups.forEach(gliderGroup => {
                                    if (gliderGroup.platform_name.equalsIgnoreCase(platform)) {
                                        // ignore time, only look at date
                                        const startDate = new Date(gliderGroup.startTime.getTime());
                                        startDate.setHours(0, 0, 0, 0);
                                        if (sfmcDate === startDate) {
                                            kmlPaths.numberedMissions[gliderGroup.deployment_number] = data;
                                            foundMatch = true;
                                        }
                                    }
                                });
                            } else if (split.length == 1) {
                                // found the most recent KML for this platform, the name is just {platform}.kml
                                foundMatch = true;
                                const platform = split[0];
                                kmlPaths.currentMissions[platform] = data;
                            } else {
                                console.warn('Malformed KML name', data.filename);
                            }
                            if (!foundMatch) {
                                console.warn('No matching mission found for KML file ', data.filename);
                            }
                        })

                        return {kmlPaths, kmlPathsOld};
                    }).catch(error => {
                        console.error(error);
                        return error;
                    });
            }).catch(error => {
                console.error(error);
                return error;
            });


    }

    public getPlotThumbnailPaths() {
        if (this.inMemory['plotThumbnailsByMission']) {
            return of(this.inMemory['plotThumbnailsByMission']).toPromise();
        } else {
            return this.http.get('/gliders/api/get_plot_thumbnails/').toPromise()
                .then(response => {
                    const img_paths = response as string[];

                    // Temperature: thumbnail_[missionNumber]_temperature.png
                    // Pot. Density: thumbnail_[missionNumber]_density.png
                    // Salinity: thumbnail_[missionNumber]_salinity.png

                    const getMatchingPathAndNumbers = function (paths, regex) {
                        const arr = img_paths.map(function (path) {
                            const matches = path.match(regex);
                            if (matches && matches.length == 2) {
                                return {
                                    number: matches[1],
                                    path: path
                                }
                            } else {
                                return null;
                            }
                        }).filter(function (obj) {
                            return !!obj;
                        });
                        const result = {};
                        arr.forEach((obj?) => {
                            if (obj) {
                                result[obj.number] = obj.path;
                            }
                        });
                        return result;
                    };

                    const temperaturePaths = getMatchingPathAndNumbers(img_paths, /([0-9]+)_temperature/);
                    const densityPaths = getMatchingPathAndNumbers(img_paths, /([0-9]+)_density/);
                    const salinityPaths = getMatchingPathAndNumbers(img_paths, /([0-9]+)_salinity/);

                    let missionNumbers = img_paths.map(function (path) {
                        const matches = path.match(/_([0-9]+)_/);
                        if (matches && matches.length == 2) {
                            return parseInt(matches[1]);
                        }
                        return null;
                    }).filter(function (val) {
                        return !!val;
                    });
                    // @ts-ignore
                    missionNumbers = Array.unique(missionNumbers);

                    const plotThumbnailsByMission = {};
                    missionNumbers.forEach((number: number) => {
                        plotThumbnailsByMission[number] = {
                            temperature: temperaturePaths[number],
                            density: densityPaths[number],
                            salinity: salinityPaths[number]
                        };
                    });

                    this.inMemory['plotThumbnailsByMission'] = plotThumbnailsByMission;

                    return plotThumbnailsByMission;
                }).catch(function (error) {
                    console.error(error);
                    return error;
                });
        }
    }

    public getMetadata() {
        return this.http.get('/gliders/api/data/').toPromise()
            .then(function success(response) {
                return response['results'];
            }).catch(function (error) {
                console.error(error);
                return error;
            });
    }
}

export interface GliderGroup {
    hasPlotThumbnails: boolean | null,
    hasErddapSet: boolean | null,
    platform_name: string,
    sea_name: string,
    status_info: string,
    deployment_number: number,
    kmlUrl: string, // KML generated by kml_tool
    kmlUrlOld: string, // KML on http://gliders.oceantrack.org/ge/
    start_time: string,
    end_time: string,
    startTime: Date;
    endTime: Date | null;
    startMoment: moment.Moment;
    endMoment: moment.Moment;
    travelDuration: TravelDuration;
}

interface TravelDuration {
    days: number;
    hours: number;
}

export interface DatasetStatus {
    mission: number,
    gliderType: string,
    start_date: any,
    end_date: any,
    status: number,
    erddapRealtimeDatasetURL: string,
    erddapDelayedDatasetURL: string;
    erddapRealtimeDatasetReason: string,
    erddapDelayedDatasetReason: string,
}
